/**
 * @fileoverview The Dark / Light switch, shared by every portal's settings.
 *
 * One component rather than one per portal so the control reads identically
 * wherever it appears; the shape is the studio's segmented control at row
 * size. A radiogroup, because that is what it is: two exclusive choices with
 * one always selected.
 */

import { Moon, Sun } from "lucide-react";
import useTheme from "@/hooks/useTheme";
import { cn } from "@/lib/utils";

const OPTIONS = [
  { id: "dark", label: "Dark", Icon: Moon },
  { id: "light", label: "Light", Icon: Sun },
];

/**
 * @param {object} props
 * @param {string} [props.className]
 * @param {string} [props.testId] Container test id; each option gets `${testId}-${id}`.
 */
export default function ThemeToggle({ className, testId = "theme-toggle" }) {
  const [theme, setTheme] = useTheme();
  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      data-testid={testId}
      className={cn(
        "inline-flex shrink-0 gap-1 rounded-full border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-1",
        className,
      )}
    >
      {OPTIONS.map(({ id, label, Icon }) => {
        const selected = theme === id;
        return (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={selected}
            data-testid={`${testId}-${id}`}
            onClick={() => setTheme(id)}
            className={cn(
              "flex items-center gap-1.5 rounded-full px-3.5 py-[7px] font-sans text-[13px] font-semibold transition-colors duration-150",
              selected
                ? "bg-[var(--accent-yellow)] text-[var(--text-inverse)]"
                : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
            )}
          >
            <Icon size={14} strokeWidth={2} aria-hidden />
            {label}
          </button>
        );
      })}
    </div>
  );
}
